import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Footer } from 'src/shared';
import { ReactComponent as XrayIcon } from 'src/assets/icons/xray.svg';
import { useAppDispatch } from 'src/hooks';
import { getMedicalDeviceKDocuments } from 'src/reducers/user';
import { getAuthToken } from 'src/utils/auth';
import { UserSelectors } from 'src/reducers/user';
import { verboseLog } from 'src/utils/config';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { MedicalDeviceCategories } from 'src/config/medicalDeviceCategories';

const XRayContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 25%, #2d2d2d 50%, #1a1a1a 75%, #0f0f0f 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(15, 15, 15, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 2px solid #404040;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid #606060;
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: #808080;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin: 0;
  color: #ffffff;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.8);
`;

const Content = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const XRayCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 2px solid #404040;
  padding: 4vh 3vw;
  max-width: 80vw;
  min-width: 300px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 30px;
    max-width: 95vw;
  }
  
  @media (min-width: 1200px) {
    padding: 50px;
    max-width: 800px;
  }
`;

const XRayIconContainer = styled.div`
  width: 8vw;
  height: 8vw;
  min-width: 60px;
  min-height: 60px;
  max-width: 120px;
  max-height: 120px;
  margin-bottom: 2vh;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
    filter: brightness(1.2) contrast(1.1);
  }
`;

const XRayTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #ffffff;
`;

const XRayDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 30px;
  opacity: 0.9;
`;

const FeatureList = styled.ul`
  text-align: left;
  font-size: 1rem;
  line-height: 1.8;
  opacity: 0.9;
`;

const XRay: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { profile } = UserSelectors();
  const isLoggedIn = Boolean(getAuthToken() && profile);
  const numDocsRender = 6;

  // Get filter parameters from URL
  const category = searchParams.get('category'); // e.g., "1"
  const subCategory = searchParams.get('subCategory'); // e.g., "A1"

  // State for documents
  const [medicalDeviceDocs, setMedicalDeviceDocs] = useState<Array<any>>();
  const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [pdfModalOpen, setPdfModalOpen] = useState<boolean>(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>('');
  const [currentPdfTitle, setCurrentPdfTitle] = useState<string>('');
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Fetch filtered documents when component mounts or filters change
  useEffect(() => {
    if (category && subCategory) {
      verboseLog('🚀 X-Ray page: Fetching filtered documents');
      verboseLog(`📡 Category: ${category}, Sub-Category: ${subCategory}`);
      
      setIsLoadingDocs(true);
      setDocsError(null);
      
      dispatch(getMedicalDeviceKDocuments({ 
        numDocs: numDocsRender,
        category: category,
        subCategory: subCategory
      })).then(
        (response) => {
          verboseLog('✅ X-Ray page: Filtered documents received');
          let data_tuples = response?.payload?.data?.data_tuples;
          verboseLog(`📋 X-Ray page: Received ${data_tuples?.length || 0} documents`);
          
          // Extract and log K numbers
          if (data_tuples && Array.isArray(data_tuples)) {
            const kNumbers = data_tuples.map((doc: any) => doc.k_number || doc.kNumber).filter(Boolean);
            verboseLog(`🔢 X-Ray page: K NUMBERS (${kNumbers.length} total):`);
            kNumbers.forEach((kNum: string, index: number) => {
              verboseLog(`   ${index + 1}. ${kNum}`);
            });
          }
          
          setMedicalDeviceDocs(data_tuples);
          setIsLoadingDocs(false);
        }
      ).catch((error) => {
        console.error('❌ X-Ray page: Failed to load filtered documents:', error);
        setDocsError(error.message || 'Failed to load documents');
        setIsLoadingDocs(false);
      });
    } else {
      console.warn('⚠️ X-Ray page: No filter parameters in URL, fetching random documents');
      setIsLoadingDocs(true);
      dispatch(getMedicalDeviceKDocuments({ numDocs: numDocsRender })).then(
        (response) => {
          let data_tuples = response?.payload?.data?.data_tuples;
          setMedicalDeviceDocs(data_tuples);
          setIsLoadingDocs(false);
        }
      ).catch((error) => {
        setDocsError(error.message || 'Failed to load documents');
        setIsLoadingDocs(false);
      });
    }
  }, [dispatch, category, subCategory]);

  // PDF modal functions (copied from medical_imaging/index.tsx)
  const openPdfModal = (pdfUrl: string, title: string) => {
    setCurrentPdfUrl(pdfUrl);
    setCurrentPdfTitle(title);
    setPdfError(null);
    setPdfModalOpen(true);
  };

  const closePdfModal = () => {
    setPdfModalOpen(false);
    setCurrentPdfUrl('');
    setCurrentPdfTitle('');
    setPdfError(null);
  };

  return (
    <XRayContainer>
      <Header>
        <BackButton onClick={() => navigate('/medical_imaging')}>
          ← Back to Medical Imaging
        </BackButton>
        <Title>X-Ray Imaging</Title>
        <div style={{ width: '120px' }}></div>
      </Header>

      <Content>
        <XRayCard>
          <XRayIconContainer>
            <XrayIcon />
          </XRayIconContainer>
          <XRayTitle>X-Ray Radiography</XRayTitle>
          <XRayDescription>
            Traditional radiographic imaging for bone examination, chest imaging, and 
            detection of various medical conditions with high diagnostic accuracy.
          </XRayDescription>
          
          <FeatureList>
            <li><strong>Bone Imaging:</strong> Fracture detection and bone structure analysis</li>
            <li><strong>Chest X-Ray:</strong> Lung and heart examination</li>
            <li><strong>Dental Imaging:</strong> Tooth and jaw structure evaluation</li>
            <li><strong>Digital Radiography:</strong> High-resolution digital imaging</li>
            <li><strong>Portable Imaging:</strong> Bedside and emergency X-ray capability</li>
            <li><strong>AI Analysis:</strong> Automated abnormality detection</li>
          </FeatureList>
        </XRayCard>

        {/* FDA Medical Device Clearance Documents Section */}
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          marginTop: '40px',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
        }}>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '700',
            marginBottom: '20px',
            color: '#ffffff',
            textAlign: 'center'
          }}>
            📊 FDA Medical Device Clearance Documents
            {category && subCategory && (
              <span style={{
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#60a5fa',
                marginLeft: '10px',
                display: 'block',
                marginTop: '5px'
              }}>
                {(() => {
                  const cat = MedicalDeviceCategories.find(c => c.categoryNumber === (category || ''));
                  const sub = cat?.subCategories.find(s => s.code === (subCategory || ''));
                  if (!cat) return '🔍 Filtered';
                  if (sub) {
                    if (sub.code === cat.code) {
                      return `🔍 Filtered: ${cat.title}`;
                    }
                    return `🔍 Filtered: ${cat.title} • ${sub.title}`;
                  }
                  return `🔍 Filtered: ${cat.title}`;
                })()}
              </span>
            )}
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            {category && subCategory 
              ? (() => {
                  const cat = MedicalDeviceCategories.find(c => c.categoryNumber === (category || ''));
                  const sub = cat?.subCategories.find(s => s.code === (subCategory || ''));
                  if (!cat) return `Showing ${medicalDeviceDocs?.length || 0} documents`;
                  if (sub) {
                    if (sub.code === cat.code) {
                      return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title}`;
                    }
                    return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title} • ${sub.title}`;
                  }
                  return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title}`;
                })()
              : 'Latest FDA medical device clearance documents from our database'}
          </p>

          {isLoadingDocs ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.7)' }}>
              <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
              Loading documents...
            </div>
          ) : docsError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
              ❌ Error: {docsError}
            </div>
          ) : medicalDeviceDocs && medicalDeviceDocs.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '25px',
              marginBottom: '30px'
            }}>
              {medicalDeviceDocs.map((doc: any, index: number) => (
                <div key={index} style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  padding: '25px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.5)';
                  e.currentTarget.style.borderColor = '#60a5fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
                >
                  {/* Document Icon */}
                  <div style={{
                    fontSize: '3rem',
                    textAlign: 'center',
                    marginBottom: '15px'
                  }}>
                    📋
                  </div>
                  
                  {/* K Number Badge */}
                  <div 
                    style={{
                      background: '#3b82f6',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      textAlign: 'center',
                      marginBottom: '15px',
                      display: 'inline-block',
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#2563eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#3b82f6';
                    }}
                    onClick={() => {
                      if (doc.pdf_url) {
                        const deviceDisplayName = doc.company_name 
                          ? `${doc.device_name || 'Unknown Device'} from ${doc.company_name}`
                          : doc.device_name || 'Device name not available';
                        openPdfModal(doc.pdf_url, `${doc.k_number} - ${deviceDisplayName}`);
                      }
                    }}
                  >
                    📄 {doc.k_number || `Document ${index + 1}`}
                  </div>
                  
                  {/* Device Name from Company */}
                  <h3 style={{
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    marginBottom: '12px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    textAlign: 'center',
                    lineHeight: '1.4'
                  }}>
                    {doc.company_name 
                      ? `${doc.device_name || 'Unknown Device'} from ${doc.company_name}`
                      : doc.device_name || 'Device name not available'
                    }
                  </h3>
                  
                  {/* Tags Row */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    justifyContent: 'center',
                    marginBottom: '12px'
                  }}>
                    {doc.device_class && (
                      <span style={{
                        fontSize: '0.7rem',
                        color: '#60a5fa',
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontWeight: '500',
                        border: '1px solid rgba(59, 130, 246, 0.3)'
                      }}>
                        {doc.device_class}
                      </span>
                    )}
                    <span style={{
                      fontSize: '0.7rem',
                      color: '#34d399',
                      backgroundColor: 'rgba(52, 211, 153, 0.2)',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontWeight: '500',
                      border: '1px solid rgba(52, 211, 153, 0.3)'
                    }}>
                      {doc.document_type === '510(k) Premarket Notification' ? '510(k)' : 
                       doc.document_type === 'PMA' ? 'PMA' : 
                       doc.document_type === 'De Novo' ? 'De Novo' : '510(k)'}
                    </span>
                    {doc.is_samd && (
                      <span style={{
                        fontSize: '0.7rem',
                        color: '#f87171',
                        backgroundColor: 'rgba(248, 113, 113, 0.2)',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontWeight: '500',
                        border: '1px solid rgba(248, 113, 113, 0.3)'
                      }}>
                        SaMD
                      </span>
                    )}
                  </div>
                  
                  {/* Intended Use */}
                  {doc.intended_use && (
                    <p style={{
                      fontSize: '0.75rem',
                      color: 'rgba(255, 255, 255, 0.6)',
                      textAlign: 'center',
                      marginBottom: '20px',
                      lineHeight: '1.3',
                      fontStyle: 'italic',
                      maxHeight: '40px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      <strong>Intended use:</strong> {doc.intended_use.length > 60 
                        ? `${doc.intended_use.substring(0, 60)}...`
                        : doc.intended_use
                      }
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255, 255, 255, 0.7)' }}>
              No documents found
            </div>
          )}
        </div>
      </Content>

      {/* PDF Modal */}
      {pdfModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '15px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #404040'
            }}>
              <h3 style={{ color: '#ffffff', margin: 0, fontSize: '1rem' }}>{currentPdfTitle}</h3>
              <button
                onClick={closePdfModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 10px'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', backgroundColor: '#2d2d2d' }}>
              {currentPdfUrl && (
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                  <Viewer
                    fileUrl={currentPdfUrl}
                    onDocumentLoad={() => {
                      // Clear any previous errors when document loads successfully
                      setPdfError(null);
                    }}
                    renderPage={(props) => (
                      <div>
                        {props.canvasLayer.children}
                        {props.textLayer && props.textLayer.children}
                      </div>
                    )}
                  />
                </Worker>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </XRayContainer>
  );
};

export default XRay;
